import { apiUrl } from "../utils/api";

/**
 * Anlagenverzeichnis aus einem DATEV-Export einspielen — zwei Schritte.
 *
 * Der Ablauf ist zustandslos: Die Vorschau gibt eine Datei-Kennung zurück, der
 * Import reicht sie mit den bestätigten Zuordnungen ein. Zwischengespeichert
 * wird nichts außer der Datei selbst; die Zahlen, die geschrieben werden,
 * rechnet der Server mit den bestätigten Regeln neu.
 */

/** Was der Import aus einem Sachkonto macht. */
export interface KontoRegel {
  riskType: "Betriebsvermögen" | "Maschine" | "Immobilie" | "KFZ";
  kategorie: string;
  vermoegensart: string;
  /** `null` = für dieses Konto wird bewusst kein Neuwert gerechnet. */
  indexsparte: string | null;
  ausschlussgrund: string | null;
}

export interface KontoBefund {
  konto: string;
  kontobezeichnung: string;
  positionen: number;
  ahk: number;
  vorschlag: KontoRegel | null;
  neuwert: number | null;
  fehler: string | null;
}

export interface AnlagenVorschau {
  token: string;
  dateiname: string;
  kontakt: string;
  zieljahr: number;
  positionen: number;
  ahkGesamt: number;
  neuwertGesamt: number;
  konten: KontoBefund[];
  /** Konten ohne Zuordnung — solange hier etwas steht, ist der Import gesperrt. */
  offen: string[];
  hinweise: string[];
  sparten: string[];
}

export interface KontoZeile {
  konto: string;
  bezeichnung: string;
  positionen: number;
  ahk: number;
  neuwert: number | null;
  indexsparte: string | null;
  ausschlussgrund: string | null;
}

export interface AnlagenImportBericht {
  datei: string;
  kontakt: string;
  zieljahr: number;
  gelesen: number;
  angelegt: number;
  uebersprungen: number;
  ahkGesamt: number;
  neuwertGesamt: number;
  ohneNeuwert: number;
  ahkOhneNeuwert: number;
  konten: KontoZeile[];
  hinweise: string[];
  dryRun: boolean;
}

/** Fehlermeldungen des Servers durchreichen — sie sagen, was zu tun ist. */
const auswerten = async (res: Response) => {
  if (res.ok) return res.json();
  let text = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (body?.message) text = Array.isArray(body.message) ? body.message.join("; ") : body.message;
  } catch {
    /* Antwort ohne JSON-Körper — dann bleibt es beim Status. */
  }
  throw new Error(text);
};

export const anlagenImportService = {
  async vorschau(file: File, kontaktId: number, jahr?: number): Promise<AnlagenVorschau> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kontaktId", String(kontaktId));
    if (jahr) fd.append("jahr", String(jahr));
    // Bewusst ohne Content-Type: den setzt der Browser samt boundary selbst.
    return auswerten(await fetch(apiUrl("anlagen-import/preview"), { method: "POST", body: fd }));
  },

  async uebernehmen(daten: {
    token: string;
    kontaktId: number;
    praefix: string;
    risikoort?: string;
    jahr?: number;
    zuordnungen: Record<string, KontoRegel>;
  }): Promise<AnlagenImportBericht> {
    return auswerten(
      await fetch(apiUrl("anlagen-import/commit"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(daten),
      }),
    );
  },
};
